import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type { ButtonHTMLAttributes, Ref } from 'react';
import { Icon } from '../../../lib/icon';
import { cn } from '../../../lib/utils';

const buttonVariants = cva(
  // `pointer-events-none` on both disabled states blocks clicks and suppresses hover/active
  // styling in one shot. asChild (`<a>`/`Link`) ignores `disabled` entirely, so aria-disabled
  // is what carries this for that case.
  'inline-flex items-center justify-center gap-2 whitespace-nowrap text-base leading-[1.4] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none aria-disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary-button text-primary-foreground hover:bg-primary-button-hover active:bg-primary-button-active disabled:opacity-40 aria-disabled:opacity-40',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive-hover active:bg-destructive-active disabled:opacity-40 aria-disabled:opacity-40',
        warning:
          'bg-warning-button text-white hover:bg-warning-button-hover active:bg-warning-button-active disabled:opacity-50 aria-disabled:opacity-50',
        outline:
          'border border-primary-button text-primary-button hover:bg-primary-button-hover hover:text-primary-foreground active:bg-primary-button-active active:text-primary-foreground disabled:opacity-40 aria-disabled:opacity-40',
        subtle:
          'border border-border-subtle bg-canvas text-foreground hover:bg-muted active:bg-border-subtle disabled:opacity-40 aria-disabled:opacity-40',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary-hover active:bg-secondary-active disabled:opacity-50 aria-disabled:opacity-50',
        white:
          'bg-white text-black hover:bg-button-white-hover active:bg-button-white-active disabled:bg-white/20 disabled:text-black/40 aria-disabled:bg-white/20 aria-disabled:text-black/40',
        ghost:
          'bg-button-ghost text-foreground hover:bg-button-ghost-hover active:bg-button-ghost-active disabled:opacity-40 aria-disabled:opacity-40',
        // No background at any state — for icon-only affordances (toggle, copy, dismiss) that
        // shouldn't read as a button. `ghost` still owns the case where a visible hover/press fill is wanted.
        plain: 'text-foreground disabled:opacity-40 aria-disabled:opacity-40',
        'ghost-dark': 'bg-white/10 text-white hover:bg-white/5 active:bg-black/5 disabled:opacity-40 aria-disabled:opacity-40',
        'outline-white':
          'border border-white text-white hover:bg-white/10 active:bg-white/20 disabled:border-white/20 disabled:text-white/20 aria-disabled:border-white/20 aria-disabled:text-white/20',
        glass:
          'bg-black/15 text-white hover:bg-black/25 active:bg-black/35 disabled:bg-black/5 disabled:text-white/40 aria-disabled:bg-black/5 aria-disabled:text-white/40',
        link: 'text-link underline-offset-4 hover:underline disabled:opacity-50 aria-disabled:opacity-50',
        'inline-link': 'underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      },
      size: {
        // Full-width on mobile, floored at 240px from `md` up. This lives on the size variants
        // rather than the base because `size-*` (icon sizes) only merges away `w-full` — a base
        // `min-w-[240px]` is a different tailwind-merge group and would survive, stretching icon
        // buttons to 240px wide at `md`. `sm` stays width-free for toolbars and inline slots.
        default: 'w-full px-4 py-3.5 md:w-auto md:min-w-[240px]',
        sm: 'px-3 py-2.5',
        lg: 'w-full px-6 py-4 md:w-auto md:min-w-[240px]',
        icon: 'size-8',
        'icon-lg': 'size-12',
      },
      shape: {
        default: 'rounded-button',
        pill: 'rounded-full',
      },
    },
    compoundVariants: [
      {
        variant: 'inline-link',
        class: 'inline p-0 font-normal whitespace-normal text-[length:inherit] leading-[inherit]',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
      shape: 'default',
    },
  },
);

type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;

export type ButtonBaseProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> & {
  variant?: VariantProps<typeof buttonVariants>['variant'];
  size?: ButtonSize;
  shape?: VariantProps<typeof buttonVariants>['shape'];
  asChild?: boolean;
  loading?: boolean;
  ref?: Ref<HTMLButtonElement>;
};

// A button with no readable text content needs an explicit accessible name. `iconOnly` is the
// discriminant for that — independent of `size`, which is purely a visual choice (e.g. a
// text button can use `size="icon"`'s square footprint, and an icon-only button can override
// the footprint via className without losing the aria-label requirement).
type ButtonProps = ButtonBaseProps & ({ iconOnly: true; 'aria-label': string } | { iconOnly?: false; 'aria-label'?: string });

function Button({
  className,
  variant,
  size,
  shape,
  asChild = false,
  loading = false,
  disabled,
  children,
  iconOnly: _iconOnly,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : 'button';
  const isDisabled = disabled || loading;

  // asChild wraps <a>/<Link>, which ignore `disabled` — use aria-disabled instead, paired
  // with the aria-disabled:* CVA variants so the visual + non-interactive behavior matches.
  return (
    <Comp
      {...props}
      className={cn(buttonVariants({ variant, size, shape, className }))}
      disabled={asChild ? undefined : isDisabled}
      aria-disabled={asChild && isDisabled ? true : undefined}
      aria-busy={loading || undefined}
      data-loading={loading || undefined}
    >
      {asChild ? (
        children
      ) : (
        <>
          {/* Always-mounted + toggled via `hidden` (not conditionally rendered) so React never
              inserts/removes a node next to `children`. A conditional sibling next to raw text
              crashes under browser translation (<font> wrapping). See WWP-423 / facebook/react#11538. */}
          <Icon name="loader" size="md" className={cn('animate-spin', { hidden: !loading })} />
          {children}
        </>
      )}
    </Comp>
  );
}

export { Button, buttonVariants };
